import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash, PencilSimple, ArrowUp, ArrowDown, FloppyDisk, ChartBar, DownloadSimple, ChartLine } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Cell, LineChart, Line, Legend } from 'recharts';
import { Loader2 } from 'lucide-react';
import {
  listTourStepsAdmin, createTourStep, updateTourStep, deleteTourStep, reorderTourSteps,
  listTourCompletions, getTeamTourProgress, getTeamTourProgressCsvUrl, getTourStepAnalytics,
  getTourStepAnalyticsHistory,
} from '../services/api';

const ROLES = [
  { key: 'super_admin', label: 'Super admin' },
  { key: 'admin', label: 'Admin' },
  { key: 'direction', label: 'Direction' },
  { key: 'user', label: 'Coopérateur / utilisateur' },
];

const TONES = [
  { key: 'from-[#2D5A4C] to-[#1E4136]', label: 'Forêt (vert profond)' },
  { key: 'from-[#00AEEF] to-[#2D5A4C]', label: 'Océan (cyan → vert)' },
  { key: 'from-[#80C51F] to-[#2D5A4C]', label: 'Prairie (lime → vert)' },
  { key: 'from-[#FF8E00] to-[#EA3C3A]', label: 'Coucher (orange → rouge)' },
  { key: 'from-[#D5007D] to-[#EA3C3A]', label: 'Hibiscus (magenta → rouge)' },
];

export default function TourAdminPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', action_path: '', action_label: '', tone: TONES[0].key, roles: [], video_url: '', playlist: [] });
  const [completions, setCompletions] = useState([]);
  const [teamMatrix, setTeamMatrix] = useState({ steps: [], team: [] });
  const [stepAnalytics, setStepAnalytics] = useState({ steps: [], total_users: 0 });
  const [history, setHistory] = useState({ steps: [], series: {}, dates: [], days_window: 30 });

  const isAdmin = user && ['super_admin', 'admin', 'direction'].includes(user.role);

  useEffect(() => {
    if (!isAdmin) return;
    listTourCompletions().then((r) => setCompletions(r.data.items || [])).catch(() => {});
    getTeamTourProgress().then((r) => setTeamMatrix(r.data || { steps: [], team: [] })).catch(() => {});
    getTourStepAnalytics().then((r) => setStepAnalytics(r.data || { steps: [], total_users: 0 })).catch(() => {});
    getTourStepAnalyticsHistory(30).then((r) => setHistory(r.data || { steps: [], series: {}, dates: [], days_window: 30 })).catch(() => {});
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    try { const r = await listTourStepsAdmin(); setItems(r.data.items || []); } catch { toast.error('Erreur de chargement'); }
    setLoading(false);
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!user) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const openCreate = () => { setEditing(null); setForm({ title: '', body: '', action_path: '', action_label: '', tone: TONES[0].key, roles: [], video_url: '', playlist: [] }); setModalOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ title: it.title || '', body: it.body || '', action_path: it.action_path || '', action_label: it.action_label || '', tone: it.tone || TONES[0].key, roles: it.roles || [], video_url: it.video_url || '', playlist: Array.isArray(it.playlist) ? it.playlist : [] }); setModalOpen(true); };

  const addPlaylistItem = () => setForm({ ...form, playlist: [...(form.playlist || []), { title: '', video_url: '' }] });
  const updatePlaylistItem = (idx, key, value) => setForm({
    ...form,
    playlist: (form.playlist || []).map((it, i) => (i === idx ? { ...it, [key]: value } : it)),
  });
  const removePlaylistItem = (idx) => setForm({
    ...form,
    playlist: (form.playlist || []).filter((_, i) => i !== idx),
  });

  const save = async () => {
    if (!form.title || form.title.length < 3) return toast.error('Titre requis (3 caractères min)');
    if (!form.body || form.body.length < 10) return toast.error('Contenu trop court');
    setSaving(true);
    try {
      const cleanPlaylist = (form.playlist || []).filter((p) => (p.title || '').trim() && (p.video_url || '').trim());
      const payload = {
        ...form,
        action_path: form.action_path || null,
        action_label: form.action_label || null,
        roles: form.roles || [],
        video_url: form.video_url || null,
        playlist: cleanPlaylist,
      };
      if (editing) await updateTourStep(editing.id, payload); else await createTourStep(payload);
      toast.success(editing ? 'Étape mise à jour' : 'Étape créée');
      setModalOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    try { await reorderTourSteps(next.map((s) => s.id)); toast.success('Ordre mis à jour'); }
    catch { toast.error('Erreur'); load(); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Supprimer « ${it.title} » ?`)) return;
    try { await deleteTourStep(it.id); toast.success('Supprimé'); load(); } catch { toast.error('Erreur'); }
  };

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-5xl" data-testid="tour-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Tour d'onboarding personnalisé</h1>
          <p className="text-sm text-[#5E6D66]">Les étapes s'affichent au premier login des nouveaux membres.</p>
        </div>
        <div className="flex items-center gap-2">
          {completions.length > 0 && (
            <div className="text-right" data-testid="tour-completions-count">
              <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold">Onboarding terminé</p>
              <p className="text-lg font-bold text-[#2D5A4C]">{completions.length} membre{completions.length > 1 ? 's' : ''}</p>
            </div>
          )}
          <Button onClick={openCreate} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="tour-new"><Plus size={14} className="mr-1" /> Nouvelle étape</Button>
        </div>
      </div>
      {loading ? (
        <div className="text-center py-10 text-[#5E6D66]"><Loader2 className="animate-spin inline mr-1" size={16}/> Chargement…</div>
      ) : items.length === 0 ? (
        <p className="text-[#5E6D66] text-center py-10">Aucune étape. Cliquez sur « Nouvelle étape » pour commencer.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <Card key={it.id} className="border-[#E8E6E1]" data-testid={`tour-card-${it.id}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`w-2 self-stretch rounded-full bg-gradient-to-b ${it.tone || TONES[0].key}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase text-[#5E6D66] font-semibold bg-[#F5F4EF] px-1.5 py-0.5 rounded">Étape {idx + 1}</span>
                    <span className="font-semibold text-[#1A2421] truncate">{it.title}</span>
                  </div>
                  <p className="text-sm text-[#5E6D66] mt-1 line-clamp-2">{it.body}</p>
                  {it.action_path && <p className="text-[10px] text-[#2D5A4C] mt-1">→ {it.action_label} ({it.action_path})</p>}
                  {it.roles?.length > 0 && (
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      {it.roles.map((r) => (
                        <span key={r} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#2D5A4C]/10 text-[#2D5A4C] font-semibold">{r}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`tour-up-${it.id}`}><ArrowUp size={14}/></Button>
                  <Button variant="ghost" size="icon" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} data-testid={`tour-down-${it.id}`}><ArrowDown size={14}/></Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`tour-edit-${it.id}`}><PencilSimple size={14}/></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(it)} className="text-red-600 hover:bg-red-50" data-testid={`tour-del-${it.id}`}><Trash size={14}/></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {stepAnalytics.steps.length > 0 && (
        <Card className="border-[#E8E6E1] mt-6" data-testid="step-analytics-chart">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-[#1A2421] uppercase tracking-wider flex items-center gap-2">
                <ChartBar size={16} weight="duotone" className="text-[#00AEEF]" />
                Taux d'engagement par étape
              </h2>
              <p className="text-[10px] text-[#5E6D66]">{stepAnalytics.total_users} utilisateurs actifs</p>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stepAnalytics.steps.map(s => ({
                  name: `#${s.order + 1} ${s.title.length > 22 ? s.title.slice(0, 22) + '…' : s.title}`,
                  taux: s.completion_rate_pct,
                  Terminés: s.completed_count,
                  'En attente': s.pending_count,
                }))} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis dataKey="name" tick={{ fill: '#5E6D66', fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={70} />
                  <YAxis tick={{ fill: '#5E6D66', fontSize: 11 }} unit="%" domain={[0, 100]} />
                  <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E8E6E1', borderRadius: 8 }} formatter={(v, k) => k === 'taux' ? [`${v}%`, 'Taux'] : [v, k]} />
                  <Bar dataKey="taux" radius={[6, 6, 0, 0]}>
                    {stepAnalytics.steps.map((s, i) => (
                      <Cell key={i} fill={s.completion_rate_pct < 40 ? '#EA3C3A' : s.completion_rate_pct < 70 ? '#FF8E00' : '#80C51F'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-4 text-[10px] text-[#5E6D66] mt-2">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-[#EA3C3A]"></span> Bloque (&lt; 40 %)</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-[#FF8E00]"></span> À surveiller (40–70 %)</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-[#80C51F]"></span> Bon (&gt; 70 %)</span>
            </div>
          </CardContent>
        </Card>
      )}

      {history.dates.length > 0 && history.steps.length > 0 && (
        <Card className="border-[#E8E6E1] mt-6" data-testid="step-analytics-history-chart">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-[#1A2421] uppercase tracking-wider flex items-center gap-2">
                <ChartLine size={16} weight="duotone" className="text-[#D5007D]" />
                Historique du taux d'engagement (30 derniers jours)
              </h2>
              <p className="text-[10px] text-[#5E6D66]">{history.dates.length} snapshot(s)</p>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={history.dates.map((d) => {
                    const row = { date: d.slice(5) }; // MM-DD
                    history.steps.forEach((s) => {
                      row[`#${s.order + 1}`] = history.series[d]?.[s.id] ?? null;
                    });
                    return row;
                  })}
                  margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis dataKey="date" tick={{ fill: '#5E6D66', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#5E6D66', fontSize: 11 }} unit="%" domain={[0, 100]} />
                  <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E8E6E1', borderRadius: 8 }} formatter={(v) => [`${v}%`, '']} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {history.steps.map((s, i) => (
                    <Line
                      key={s.id}
                      type="monotone"
                      dataKey={`#${s.order + 1}`}
                      name={`#${s.order + 1} ${s.title.slice(0, 20)}`}
                      stroke={['#2D5A4C', '#00AEEF', '#FF8E00', '#D5007D', '#80C51F', '#EA3C3A', '#5E6D66'][i % 7]}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-[#5E6D66] italic mt-2">Snapshots enregistrés chaque nuit à 2 h UTC. Le premier point apparaît dès le lendemain d'un tour terminé.</p>
          </CardContent>
        </Card>
      )}

      {teamMatrix.team.length > 0 && teamMatrix.steps.length > 0 && (
        <Card className="border-[#E8E6E1] mt-6" data-testid="team-progress-matrix">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-[#1A2421] uppercase tracking-wider">Matrice équipe × étapes</h2>
              <a
                href={getTeamTourProgressCsvUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#2D5A4C] hover:text-[#1E4136] px-3 py-1.5 rounded-md border border-[#2D5A4C]/30 hover:bg-[#F5F4EF] transition-colors"
                data-testid="matrix-csv-export"
              >
                <DownloadSimple size={12} weight="bold" /> Export CSV
              </a>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#E8E6E1]">
                    <th className="text-left py-2 pr-3 font-semibold text-[#5E6D66] uppercase text-[10px] tracking-wider">Membre</th>
                    <th className="text-left py-2 pr-3 font-semibold text-[#5E6D66] uppercase text-[10px] tracking-wider">Rôle</th>
                    {teamMatrix.steps.map((s) => (
                      <th key={s.id} className="text-center py-2 px-1 font-semibold text-[#5E6D66] uppercase text-[9px] tracking-wider" title={s.title}>
                        <div className="w-6 h-6 mx-auto rounded-full bg-gradient-to-br from-[#2D5A4C] to-[#00AEEF] text-white flex items-center justify-center text-[10px] font-bold">
                          {s.order + 1}
                        </div>
                      </th>
                    ))}
                    <th className="text-right py-2 pl-3 font-semibold text-[#5E6D66] uppercase text-[10px] tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMatrix.team.map((u) => {
                    const pct = u.visible_count > 0 ? Math.round((u.completed_count / u.visible_count) * 100) : 0;
                    return (
                      <tr key={u.user_id} className="border-b border-[#E8E6E1] hover:bg-[#F9F8F5]" data-testid={`team-row-${u.user_id}`}>
                        <td className="py-2 pr-3 font-semibold text-[#1A2421] truncate max-w-[160px]">{u.name}</td>
                        <td className="py-2 pr-3 text-[#5E6D66] text-[10px] capitalize">{(u.role || '').replace('_', ' ')}</td>
                        {teamMatrix.steps.map((s) => {
                          const done = u.completed_ids.includes(s.id);
                          const applicable = !s.roles || s.roles.length === 0 || s.roles.includes(u.role);
                          if (!applicable) return <td key={s.id} className="text-center py-2 px-1 text-[#E8E6E1]">—</td>;
                          return (
                            <td key={s.id} className="text-center py-2 px-1">
                              {done
                                ? <span className="inline-block w-5 h-5 rounded-full bg-[#80C51F] text-white text-[10px] font-bold leading-5">✓</span>
                                : <span className="inline-block w-5 h-5 rounded-full border-2 border-[#E8E6E1]"></span>}
                            </td>
                          );
                        })}
                        <td className="text-right py-2 pl-3">
                          <div className="inline-flex items-center gap-1.5">
                            <div className="w-14 h-1.5 rounded-full bg-[#E8E6E1] overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-[#80C51F] to-[#2D5A4C]" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="font-mono text-[10px] font-bold text-[#2D5A4C] w-8">{u.completed_count}/{u.visible_count}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-[#5E6D66] italic mt-3">Les tirets indiquent une étape non applicable au rôle de l'utilisateur.</p>
          </CardContent>
        </Card>
      )}

      {completions.length > 0 && (
        <Card className="border-[#E8E6E1] mt-6" data-testid="tour-completions-list">
          <CardContent className="p-4">
            <h2 className="text-sm font-bold text-[#1A2421] uppercase tracking-wider mb-3">Derniers onboarding terminés</h2>
            <div className="space-y-1.5">
              {completions.slice(0, 20).map((c) => (
                <div key={c.user_id} className="flex items-center justify-between text-xs py-1.5 border-b border-[#E8E6E1] last:border-0">
                  <div>
                    <span className="font-semibold text-[#1A2421]">{c.user_name}</span>
                    <span className="text-[#5E6D66] ml-2">· {c.user_role}</span>
                  </div>
                  <span className="text-[10px] text-[#5E6D66]">{new Date(c.completed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="tour-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Modifier l\'étape' : 'Nouvelle étape'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="tour-form-title"/></div>
            <div><Label>Contenu</Label><Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="tour-form-body"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Chemin d'action (optionnel)</Label><Input value={form.action_path} onChange={(e) => setForm({ ...form, action_path: e.target.value })} placeholder="/dossiers"/></div>
              <div><Label>Libellé du bouton</Label><Input value={form.action_label} onChange={(e) => setForm({ ...form, action_label: e.target.value })} placeholder="Voir les dossiers"/></div>
            </div>
            <div>
              <Label>Vidéo tutoriel (optionnel)</Label>
              <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtu.be/... ou lien MP4" data-testid="tour-video-url" />
              <p className="text-[10px] text-[#5E6D66] italic mt-1">YouTube (auto-embed) ou lien direct MP4/WebM.</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Playlist vidéos (optionnel)</Label>
                <Button size="sm" variant="outline" type="button" onClick={addPlaylistItem} data-testid="tour-playlist-add">
                  <Plus size={12} className="mr-1" /> Ajouter une vidéo
                </Button>
              </div>
              {(form.playlist || []).length === 0 ? (
                <p className="text-[10px] text-[#5E6D66] italic">Regroupez plusieurs vidéos par thème pour offrir un parcours complet à l'utilisateur.</p>
              ) : (
                <div className="space-y-2">
                  {form.playlist.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-[#F9F8F5] rounded-lg p-2" data-testid={`tour-playlist-${idx}`}>
                      <span className="text-[10px] font-bold text-[#5E6D66] w-4">{idx + 1}</span>
                      <Input
                        className="h-8 text-xs"
                        placeholder="Titre de la vidéo"
                        value={it.title || ''}
                        onChange={(e) => updatePlaylistItem(idx, 'title', e.target.value)}
                        data-testid={`tour-playlist-title-${idx}`}
                      />
                      <Input
                        className="h-8 text-xs flex-1"
                        placeholder="URL YouTube ou MP4"
                        value={it.video_url || ''}
                        onChange={(e) => updatePlaylistItem(idx, 'video_url', e.target.value)}
                        data-testid={`tour-playlist-url-${idx}`}
                      />
                      <Button size="icon" variant="ghost" onClick={() => removePlaylistItem(idx)} className="h-8 w-8 text-red-600" data-testid={`tour-playlist-remove-${idx}`}>
                        <Trash size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Rôles autorisés</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ROLES.map((r) => {
                  const checked = form.roles.includes(r.key);
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setForm({ ...form, roles: checked ? form.roles.filter((x) => x !== r.key) : [...form.roles, r.key] })}
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold border transition-colors ${checked ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white text-[#5E6D66] border-[#E8E6E1]'}`}
                      data-testid={`tour-role-${r.key}`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#5E6D66] italic mt-1">Laissez vide pour afficher à tous les utilisateurs.</p>
            </div>
            <div>
              <Label>Ambiance de couleur</Label>
              <select className="w-full h-9 rounded-md border border-[#E8E6E1] bg-white px-2 text-sm" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}>
                {TONES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <div className={`mt-2 h-2 rounded-full bg-gradient-to-r ${form.tone}`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Annuler</Button>
            <Button disabled={saving} onClick={save} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="tour-form-save">
              {saving ? <Loader2 className="animate-spin mr-1" size={14}/> : <FloppyDisk size={14} className="mr-1"/>}{editing ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
